import type {
    BetaPage,
    BetaPagePreview,
    BetaPost,
} from '@/types/beta-dashboard'
import { isExternalLinkPage } from '@/utils/page'

const MEDIA_FRAME_TYPES = new Set([
    'beforeafterimage',
    'before-after-image',
    'before_after_image',
    'gallery',
    'image',
])

const MEDIA_KEYS = [
    'afterPicture',
    'background_image',
    'backgroundImage',
    'beforePicture',
    'coverImage',
    'image',
    'mainImage',
    'src',
    'thumbnail',
    'url',
    'uri',
]

const isRecord = (value: unknown): value is Record<string, unknown> => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
)

const normalizedMediaString = (value: unknown) => {
    if (typeof value !== 'string') return null

    const normalized = value.trim()
    return normalized || null
}

const findMediaValue = (value: unknown): string | null => {
    const directValue = normalizedMediaString(value)
    if (directValue) return directValue

    if (Array.isArray(value)) {
        for (const item of value) {
            const media = findMediaValue(item)
            if (media) return media
        }

        return null
    }

    if (!isRecord(value)) return null

    for (const key of [...MEDIA_KEYS, 'content']) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) continue

        const media = findMediaValue(value[key])
        if (media) return media
    }

    return null
}

export const extractFirstBetaPageMedia = (value: unknown): string | null => {
    if (Array.isArray(value)) {
        for (const item of value) {
            const media = extractFirstBetaPageMedia(item)
            if (media) return media
        }

        return null
    }

    if (!isRecord(value)) return null

    const frameType = String(value.frameType ?? '').trim().toLocaleLowerCase()
    if (MEDIA_FRAME_TYPES.has(frameType)) {
        const frameMedia = findMediaValue(value.frameContent)
        if (frameMedia) return frameMedia
    }

    for (const key of MEDIA_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) continue

        const media = findMediaValue(value[key])
        if (media) return media
    }

    for (const nestedValue of Object.values(value)) {
        const media = extractFirstBetaPageMedia(nestedValue)
        if (media) return media
    }

    return null
}

export const deriveBetaPagePreview = (
    page: BetaPage,
    document: unknown,
    posts: BetaPost[] = [],
): BetaPagePreview => {
    const kind: BetaPagePreview['kind'] = page.isHome
        ? 'home'
        : page.listPages == 1
            ? 'list'
            : isExternalLinkPage(page)
                ? 'link'
                : 'content'

    if (kind === 'list') {
        const image = posts[0]?.mainImage ?? null

        return {
            pageId: page.id,
            kind,
            image,
            source: image ? 'post' : 'fallback',
        }
    }

    if (kind !== 'link') {
        const image = extractFirstBetaPageMedia(document)

        return {
            pageId: page.id,
            kind,
            image,
            source: image ? 'document' : 'fallback',
        }
    }

    return {
        pageId: page.id,
        kind,
        image: null,
        source: 'fallback',
    }
}
